// src/components/LocationMap.js
import React, { useEffect, useMemo, useRef } from 'react';
import './LocationMap.css';

const LocationMap = ({ coordinates, address, title, isVirtual = false, locations = [] }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const leafletLoadedRef = useRef(false);

  // Load Leaflet only once - this hook runs unconditionally
  useEffect(() => {
    // Skip loading Leaflet if it's already loaded
    if (window.L) {
      leafletLoadedRef.current = true;
      return;
    }

    if (window.leafletLoading) return;
    window.leafletLoading = true;

    // Add Leaflet CSS
    const linkEl = document.createElement('link');
    linkEl.rel = 'stylesheet';
    linkEl.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(linkEl);

    // Load Leaflet script
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.async = true;
    script.onload = () => {
      leafletLoadedRef.current = true;
      window.leafletLoading = false;
    };
    document.head.appendChild(script);
  }, []);

  const markerPoints = useMemo(() => {
    if (Array.isArray(locations) && locations.length > 0) {
      return locations
        .map((loc) => {
          const lat = loc?.coordinates?.lat;
          const lng = loc?.coordinates?.lng;
          if (typeof lat === 'number' && typeof lng === 'number') {
            return {
              lat,
              lng,
              address: loc.address || ''
            };
          }
          return null;
        })
        .filter(Boolean);
    }

    if (
      coordinates &&
      typeof coordinates.lat === 'number' &&
      typeof coordinates.lng === 'number'
    ) {
      return [
        {
          lat: coordinates.lat,
          lng: coordinates.lng,
          address: address || ''
        }
      ];
    }

    return [];
  }, [locations, coordinates, address]);

  // Initialize map with delay to ensure DOM is ready - this hook runs unconditionally
  useEffect(() => {
    // Skip map initialization for virtual events or if no coordinates
    if (isVirtual || markerPoints.length === 0) return;

    const initMap = () => {
      if (!leafletLoadedRef.current || !mapContainerRef.current) {
        setTimeout(initMap, 100);
        return;
      }

      // Clean up previous map instance if it exists
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      try {
        // Create map with a try-catch to handle potential errors
        mapRef.current = window.L.map(mapContainerRef.current, {
          zoomControl: true,
          scrollWheelZoom: false, // Disable scroll wheel zoom to avoid issues
          attributionControl: true
        });

        // Add tile layer
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(mapRef.current);

        // Only set view and add marker after a small delay to ensure map is ready
        setTimeout(() => {
          if (mapRef.current && markerPoints.length > 0) {
            if (markerPoints.length === 1) {
              const point = markerPoints[0];
              mapRef.current.setView([point.lat, point.lng], 15);
            } else {
              const bounds = window.L.latLngBounds(markerPoints.map(point => [point.lat, point.lng]));
              mapRef.current.fitBounds(bounds, { padding: [30, 30] });
            }

            markerPoints.forEach((point) => {
              window.L.marker([point.lat, point.lng])
                .addTo(mapRef.current)
                .bindPopup(`<strong>${title || 'Location'}</strong><br>${point.address || ''}`);
            });
          }
        }, 100);

        // Ensure map is properly sized
        setTimeout(() => {
          if (mapRef.current) {
            mapRef.current.invalidateSize();
          }
        }, 300);
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };

    // Delay initialization to ensure DOM is ready
    setTimeout(initMap, 200);

    // Cleanup when unmounting
    return () => {
      if (mapRef.current) {
        try {
          mapRef.current.remove();
        } catch (e) {
          console.error('Error removing map:', e);
        }
        mapRef.current = null;
      }
    };
  }, [markerPoints, title, isVirtual]);

  // Generate directions URL for Google Maps
  const getDirectionsUrl = () => {
    const firstPoint = markerPoints[0];
    if (!firstPoint) return '#';
    // Use Google Maps directions URL format (free to link to)
    return `https://www.google.com/maps/dir/?api=1&destination=${firstPoint.lat},${firstPoint.lng}&travelmode=driving`;
  };

  // Render a virtual map instead of a real one for virtual listings
  if (isVirtual) {
    return (
      <div className="location-map-container">
        <div className="location-map virtual-map" data-testid="map-container">
          <div className="virtual-label">
            <svg xmlns="http://www.w3.org/2000/svg" className="virtual-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
              <line x1="8" y1="21" x2="16" y2="21"></line>
              <line x1="12" y1="17" x2="12" y2="21"></line>
            </svg>
            <span>Virtual Event</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="location-map-container">
      <div 
        className="location-map" 
        ref={mapContainerRef} 
        data-testid="map-container"
      ></div>
      {markerPoints.length > 0 && (
        <div className="location-map-actions">
          <a 
            href={getDirectionsUrl()} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="directions-button"
          >
            Get Directions in Google Maps
          </a>
        </div>
      )}
    </div>
  );
};

export default LocationMap;
